import {
  BaseStimulator,
  type StimulatorKind,
  type StimulusRequest,
  type StimulusResult,
} from './Stimulator.ts';

/**
 * ============================================================================
 * ADAPTER FOR A BACKYARD BRAINS HUMAN-HUMAN INTERFACE. NOT VERIFIED. NOT SAFE
 * TO USE ON A PERSON UNTIL A QUALIFIED OPERATOR HAS COMPLETED THE CHECKLIST IN
 * docs/HARDWARE.md.
 * ============================================================================
 *
 * ## What this file is
 *
 * The single place where anything Backyard Brains-specific is allowed to live.
 * Everything above it -- game, bot, controllers, logging, dashboard -- talks to
 * the `Stimulator` interface and has no idea this file exists. Swapping
 * `SimulatedHumanStimulator` for this one is a change in `app/Modes.ts` and
 * nowhere else.
 *
 * ## What this file deliberately is NOT
 *
 * It is not an implementation of a Backyard Brains control protocol, because
 * this project has not verified that a documented one exists for the HHI's
 * stimulation output. The HHI is an analog instrument: stimulation intensity is
 * set with a physical dial, and the unit fires when its trigger input is
 * driven. There is no invented serial command here that claims to set voltage,
 * current, pulse width, frequency, or electrode configuration, and none must
 * ever be added. If you find yourself writing `SET_INTENSITY`, stop.
 *
 * ## The integration this adapter assumes
 *
 *     browser  --WebSerial-->  operator's trigger microcontroller
 *                                     |
 *                                     | closes / drives the HHI trigger input
 *                                     v
 *                              Backyard Brains HHI  (intensity set by hand)
 *                                     |
 *                                     v
 *                            participant's forearm moves
 *                                     |
 *                                     v
 *                        switch / EMG -> InputDevice -> game
 *
 * The command set below is this adapter's protocol to *that microcontroller*,
 * which the operator writes and flashes. It carries timing only:
 *
 *   PING            -> "PONG <firmware>"     liveness check
 *   ARM             -> "ARMED"               enable the trigger output
 *   DISARM          -> "DISARMED"            disable it
 *   TRIG <ms>       -> "TRIG <ms> OK"        drive the trigger for <ms>
 *   STOP            -> "STOPPED"             immediate abort, latch off
 *
 * Duration is the only parameter that crosses this boundary. Everything that
 * determines what the stimulation *feels like* stays on the hardware, set by a
 * human, per the manufacturer's documented procedure.
 *
 * ## Before this is used with a participant
 *
 * See docs/HARDWARE.md. In summary: confirm the trigger interface against the
 * manufacturer's documentation for your unit, confirm the firmware's own
 * independent duration and rate limits, confirm the physical emergency stop,
 * set intensity by hand with the participant present and consenting, and get
 * whatever ethics approval your institution requires. The software limits in
 * `SafetyGate` are a backstop for software bugs. They are not a safety system.
 */

export interface HHIAdapterOptions {
  /**
   * Must be set to true by an operator who has completed docs/HARDWARE.md.
   * Without it, `connect()` throws. This is a speed bump against someone
   * selecting "HHI" from a dropdown to see what happens.
   */
  operatorAcknowledgedUnverified: boolean;
  /** Note recorded in the session log describing the hand-set intensity. */
  operatorIntensityNote: string;
  baudRate: number;
  /** Time allowed for the firmware to answer a command, ms. */
  commandTimeoutMs: number;
  /** Reuse an already-granted serial port instead of prompting. */
  port?: SerialPort;
}

export const DEFAULT_HHI_OPTIONS: HHIAdapterOptions = {
  operatorAcknowledgedUnverified: false,
  operatorIntensityNote: 'NOT RECORDED',
  baudRate: 115200,
  commandTimeoutMs: 500,
};

export class BackyardBrainsHHIAdapter extends BaseStimulator {
  readonly kind: StimulatorKind = 'backyard-brains-hhi';
  readonly label = 'Backyard Brains HHI (via trigger interface)';
  /** The only stimulator in the project that can energise real electrodes. */
  readonly simulated = false;

  readonly options: HHIAdapterOptions;

  private port: SerialPort | null = null;
  private writer: WritableStreamDefaultWriter<Uint8Array> | null = null;
  private reader: ReadableStreamDefaultReader<Uint8Array> | null = null;
  private buffer = '';
  private pending: { resolve: (line: string) => void; reject: (e: Error) => void } | null = null;
  private firmware = 'unknown';
  private stopping = false;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;

  constructor(options: Partial<HHIAdapterOptions> = {}) {
    super();
    this.options = { ...DEFAULT_HHI_OPTIONS, ...options };
  }

  static isSupported(): boolean {
    return typeof navigator !== 'undefined' && 'serial' in navigator;
  }

  async connect(): Promise<void> {
    if (!BackyardBrainsHHIAdapter.isSupported()) {
      throw new Error('WebSerial is not available in this browser');
    }

    this.stopping = false;
    this.port = this.options.port ?? (await navigator.serial.requestPort());
    await this.port.open({ baudRate: this.options.baudRate });

    if (!this.port.writable || !this.port.readable) {
      throw new Error('Serial port opened without readable/writable streams');
    }
    this.writer = this.port.writable.getWriter();
    this.reader = this.port.readable.getReader();
    void this.readLoop();

    const pong = await this.command('PING');
    if (!pong.startsWith('PONG')) {
      await this.disconnect();
      throw new Error(`Unexpected response to PING: ${pong}`);
    }
    this.firmware = pong.slice(4).trim() || 'unknown';

    // Connected is not armed. A freshly connected stimulator can do nothing.
    this.connected = true;
    this.armed = false;
    this.heartbeatTimer = setInterval(() => {
      if (this.stopping || !this.writer || this.pending) return;
      void this.command('PING').then((reply) => {
        if (!reply.startsWith('PONG')) this.armed = false;
      }).catch(() => {
        // The ESP32 independently disarms when heartbeats stop. Mirror that
        // fail-closed state locally instead of claiming hardware is still live.
        this.armed = false;
      });
    }, 1000);
  }

  async disconnect(): Promise<void> {
    this.stopping = true;
    if (this.heartbeatTimer !== null) clearInterval(this.heartbeatTimer);
    this.heartbeatTimer = null;
    if (this.armed) {
      try {
        await this.command('DISARM');
      } catch {
        // Best effort; the port is going away regardless.
      }
    }
    this.armed = false;
    this.connected = false;
    try {
      await this.reader?.cancel();
    } catch { /* already closed */ }
    this.reader = null;
    try {
      this.writer?.releaseLock();
    } catch { /* already released */ }
    this.writer = null;
    try {
      await this.port?.close();
    } catch { /* already closed */ }
    this.port = null;
  }

  override async arm(): Promise<void> {
    await super.arm();
    try {
      const reply = await this.command('ARM');
      if (!reply.startsWith('ARMED')) {
        this.armed = false;
        throw new Error(`Firmware refused ARM: ${reply}`);
      }
    } catch (err) {
      this.armed = false;
      throw err;
    }
  }

  override async disarm(): Promise<void> {
    this.armed = false;
    try {
      await this.command('DISARM');
    } catch {
      // A disarm that could not be confirmed still leaves the software disarmed.
      // The operator-facing status will show the transport error.
    }
  }

  async requestStimulus(request: StimulusRequest): Promise<StimulusResult> {
    const denied = this.guard(request);
    if (denied) return denied;

    this.requestCount++;
    this.lastRequestAt = request.requestedAt;

    const durationMs = Math.round(request.durationMs);
    const startedAt = performance.now();
    try {
      const reply = await this.command(`TRIG ${durationMs}`);
      if (!/^TRIG \d+ OK$/.test(reply)) {
        this.rejectedCount++;
        return {
          requestId: request.requestId,
          accepted: false,
          rejectedReason: `firmware refused: ${reply}`,
        };
      }
      this.acceptedCount++;
      this.deliveredCount++;
      this.lastStimulusAt = startedAt;
      return {
        requestId: request.requestId,
        accepted: true,
        startedAt,
        endedAt: startedAt + durationMs,
      };
    } catch (err) {
      this.rejectedCount++;
      return {
        requestId: request.requestId,
        accepted: false,
        rejectedReason: 'transport error',
        error: String(err),
      };
    }
  }

  /**
   * Fire-and-forget STOP. Synchronous by contract, so it cannot await the
   * firmware's reply -- it writes the bytes and latches the software state.
   * The hardware's own stop remains the mechanism that is relied upon.
   */
  override emergencyStop(): void {
    super.emergencyStop();
    if (this.writer) {
      void this.writer.write(new TextEncoder().encode('STOP\n')).catch(() => undefined);
    }
  }

  override getStatus() {
    return {
      ...super.getStatus(),
      operatorIntensityNote: this.options.operatorIntensityNote,
    };
  }

  protected override detail(): Record<string, unknown> {
    return {
      firmware: this.firmware,
      baudRate: this.options.baudRate,
      verified: false,
      warning: 'unverified adapter; see docs/HARDWARE.md',
    };
  }

  // ------------------------------------------------------------- transport --

  private async command(text: string): Promise<string> {
    if (!this.writer) throw new Error('not connected');
    if (this.pending) throw new Error('a command is already in flight');

    const reply = new Promise<string>((resolve, reject) => {
      const timer = setTimeout(() => {
        this.pending = null;
        reject(new Error(`timeout waiting for reply to "${text}"`));
      }, this.options.commandTimeoutMs);
      this.pending = {
        resolve: (line) => {
          clearTimeout(timer);
          this.pending = null;
          resolve(line);
        },
        reject: (e) => {
          clearTimeout(timer);
          this.pending = null;
          reject(e);
        },
      };
    });

    await this.writer.write(new TextEncoder().encode(`${text}\n`));
    return reply;
  }

  private async readLoop(): Promise<void> {
    const reader = this.reader;
    if (!reader) return;
    const decoder = new TextDecoder();
    try {
      while (!this.stopping) {
        const { value, done } = await reader.read();
        if (done) break;
        if (!value) continue;
        this.buffer += decoder.decode(value, { stream: true });
        let nl: number;
        while ((nl = this.buffer.indexOf('\n')) >= 0) {
          const line = this.buffer.slice(0, nl).trim();
          this.buffer = this.buffer.slice(nl + 1);
          if (line) this.pending?.resolve(line);
        }
        if (this.buffer.length > 4096) this.buffer = '';
      }
    } catch (err) {
      this.pending?.reject(err instanceof Error ? err : new Error(String(err)));
    } finally {
      try {
        reader.releaseLock();
      } catch { /* already released */ }
    }
  }
}
